// Client-side API utility for frontend interactions
// Converted from frontend/lib/api-client.js

interface APIClientConfig {
  baseURL?: string;
  timeout?: number;
}

interface APIResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

interface RPCRequest {
  id: number;
  method: string;
  params: any;
  jsonrpc: string;
}

interface RPCResponse<T = any> {
  id: number;
  result?: T;
  error?: {
    code: number;
    message: string;
    data?: any;
  };
  jsonrpc: string;
}

export class ClientAPIClient {
  private baseURL: string;
  private headers: Record<string, string>;
  private rpcEndpoint: string;
  private rpcBatchEndpoint: string;
  private requestId: number = 0;
  private timeout: number;

  constructor(config: APIClientConfig = {}) {
    // Use provided baseURL or detect from window location
    this.baseURL = config.baseURL || this.getDefaultBaseURL();
    this.timeout = config.timeout || 30000;
    
    this.headers = {
      'Content-Type': 'application/json',
    };
    
    // RPC endpoints
    this.rpcEndpoint = `${this.baseURL}/api/rpc`;
    this.rpcBatchEndpoint = `${this.baseURL}/api/rpc/batch`;
  }

  private getDefaultBaseURL(): string {
    if (typeof window !== 'undefined') {
      return `${window.location.protocol}//${window.location.host}`;
    }
    return 'http://localhost:8787'; // Fallback for SSR
  }

  // Get authentication headers
  private getAuthHeaders(): Record<string, string> {
    const authHeaders: Record<string, string> = {};
    
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('access_token');
      if (token) {
        authHeaders['Authorization'] = `Bearer ${token}`;
      }
    }
    
    return authHeaders;
  }

  // Handle authentication errors
  private handleAuthError(response: Response): void {
    if (response.status === 401 || response.status === 403) {
      console.warn('Authentication failed. Clearing stored token.');
      
      if (typeof window !== 'undefined') {
        localStorage.removeItem('access_token');
        
        // Dispatch auth failed event
        window.dispatchEvent(new CustomEvent('auth-failed', { 
          detail: { status: response.status } 
        }));
      }
      
      throw new Error(`Authentication failed (${response.status}). Please log in again.`);
    }
  }

  // Generic HTTP request method
  private async request<T = any>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    const requestOptions: RequestInit = {
      ...options,
      headers: {
        ...this.headers,
        ...this.getAuthHeaders(),
        ...options.headers,
      },
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...requestOptions,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle authentication errors
      this.handleAuthError(response);

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return {
        success: true,
        data,
      };
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          return {
            success: false,
            error: 'Request timeout',
          };
        }
        return {
          success: false,
          error: error.message,
        };
      }
      return {
        success: false,
        error: 'Unknown error occurred',
      };
    }
  }

  // HTTP method shortcuts
  async get<T = any>(endpoint: string): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T = any>(endpoint: string, data?: any): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = any>(endpoint: string, data?: any): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(endpoint: string): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // RPC method call
  async rpc<T = any>(method: string, params: any = {}): Promise<T> {
    const requestId = ++this.requestId;
    const rpcRequest: RPCRequest = {
      id: requestId,
      method,
      params,
      jsonrpc: '2.0',
    };

    const response = await this.request<RPCResponse<T>>(this.rpcEndpoint, {
      method: 'POST',
      body: JSON.stringify(rpcRequest),
    });

    if (!response.success) {
      throw new Error(response.error || 'RPC request failed');
    }

    const rpcResponse = response.data!;
    
    if (rpcResponse.error) {
      throw new Error(rpcResponse.error.message || 'RPC error');
    }

    return rpcResponse.result!;
  }

  // Batch RPC calls
  async rpcBatch<T = any>(requests: Array<{ method: string; params?: any }>): Promise<T[]> {
    const rpcRequests: RPCRequest[] = requests.map((req) => ({
      id: ++this.requestId,
      method: req.method,
      params: req.params || {},
      jsonrpc: '2.0',
    }));

    const response = await this.request<RPCResponse<T>[]>(this.rpcBatchEndpoint, {
      method: 'POST',
      body: JSON.stringify(rpcRequests),
    });

    if (!response.success) {
      throw new Error(response.error || 'Batch RPC request failed');
    }

    const rpcResponses = response.data!;
    
    return rpcResponses.map((rpcResponse) => {
      if (rpcResponse.error) {
        throw new Error(rpcResponse.error.message || 'RPC error');
      }
      return rpcResponse.result!;
    });
  }
}

// Export singleton instance
export const apiClient = new ClientAPIClient();